import { isAndroid, isIOS, isWeixin } from '../../utils/ua'

/**
 * 「打开 App」分层跳转(TAPD 1006913,策略见实施方案 §1 第 7 条 / §6 第 5 条)
 *
 *   微信内     → 微信禁 scheme、也拦第三方 universal link,唯一出路是引导
 *                「右上角 ··· → 在浏览器打开」;想直接下载则给应用宝(它在微信内能打开)
 *   iOS 浏览器 → 导航到 **web-gateway 上的分享链接**,由 iOS 拦截成 universal link
 *                唤起 App 并直达该视频;没装则 gateway 302 回本页,靠标记位给 App Store 按钮
 *   安卓浏览器 → 自定义 scheme → 超时试本机应用市场 market:// → 再超时落应用宝
 *
 * 顺序必须是 scheme 优先:唤起已装的 App 才是最优解,先跳市场就把人送走了。
 * 「有没有走掉」用页面可见性判断(唤起成功时浏览器会切到后台)。
 */
export const APP_STORE_URL = 'https://apps.apple.com/cn/app/id1284007492'
export const YINGYONGBAO_URL = 'https://a.app.qq.com/o/simple.jsp?pkgname=com.ihealth.HuTang'
export const ANDROID_MARKET_URL = 'market://details?id=com.ihealth.HuTang'
export const APP_SCHEME_PREFIX = 'hutang://video/'

/**
 * universal link 的域名。**必须是 web-gateway 这个域名**,不能用当前页的 h5-blogs 域名:
 * App 的 needle.entitlements 里 applinks 只授权了 wechat-share / hh / hh-stg 三个,
 * h5-blogs 不在其中 —— iOS 根本不会去读它的 AASA,导航就会被当成普通跳转真的加载掉,
 * 结果是落地页自己刷新一遍,永远唤不起 App(2026-09-04 查 entitlements 发现)。
 * 顺带:跳当前页自己也不触发 UL(iOS 不在同域导航上触发),所以那种写法双重失效。
 *
 * 环境靠路径前缀区分,与 App 侧 videoShareLink.js 拼链接的规则一致;
 * gateway 只有这一个生产实例(没有测试环境的 gateway)。
 */
export const UL_ORIGIN = 'https://wechat-share.ihealthlabs.com.cn'

// 没装 App 时 gateway 会 302 回本页,页面一重载定时器就没了,引导按钮永远弹不出来。
// 所以跳之前先在 sessionStorage 记一笔,重载后读到就直接给 App Store 入口。
export const IOS_PENDING_KEY = 'video_share_ios_ul_pending'

const SCHEME_TIMEOUT = 1500 // 安卓唤起 App 的等待窗口(经验值)
const MARKET_TIMEOUT = 1500 // market:// 之后再等一轮才落应用宝
const IOS_UL_TIMEOUT = 2000 // iOS universal link 没走掉就认为没装

export const buildSchemeUrl = videoId => `${APP_SCHEME_PREFIX}${videoId}`

/**
 * 微信引导框里「还没有 App?点此下载」的去处。
 *
 * 实施方案 §1 第 7 条定的是「**iOS 一律 App Store**;Android 微信内→应用宝」。
 * 2026-09-10 之前这里不分平台一律给应用宝 —— iOS 上虽然应用宝的 simple.jsp 会自己
 * 识别再跳 App Store,但白绕一跳、中间那页体验也差,与定的口径不符。
 *
 * 安卓仍必须是应用宝:`market://` 和其它市场链接在微信内点了没反应,
 * 应用宝是腾讯自家的,是微信里唯一能正常打开的分发页。
 */
export const wechatDownloadUrl = () => (isIOS() ? APP_STORE_URL : YINGYONGBAO_URL)

// 测试环境的落地页域名(与 actions/queries.js 的判定同源)
const isTestHost = () => {
  const host = (typeof window !== 'undefined' && window.location && window.location.hostname) || ''
  return host.indexOf('h5-blogs-test') > -1 || host.indexOf('h5-blogs-dev') > -1 || host === 'localhost' || host === '127.0.0.1'
}

export const buildUniversalLink = videoId => `${UL_ORIGIN}/${isTestHost() ? 'dev' : 'prod'}/videoShare/${videoId}`

const session = () => {
  try {
    return typeof window !== 'undefined' ? window.sessionStorage : null
  } catch (e) {
    // 隐私模式下访问 sessionStorage 会直接抛
    return null
  }
}

const markIosPending = () => {
  const s = session()
  if (s) {
    try {
      s.setItem(IOS_PENDING_KEY, '1')
    } catch (e) {}
  }
}

/**
 * 撤销标记。**唤起成功时必须调** —— 那种情况页面不会重载(App 接管了、本页留在后台),
 * consumeIosPending 没有机会读到它,标记就一直留在 sessionStorage 里。后果:同一个
 * 浏览器 session 里下次打开任意 /videoShare/*(再点一条分享、或从微信「在浏览器打开」
 * 再进一次),页面一挂载就弹「还没有安装共同照护 App」—— 而用户明明装着。
 */
export const clearIosPending = () => {
  const s = session()
  if (!s) return
  try {
    s.removeItem(IOS_PENDING_KEY)
  } catch (e) {}
}

/**
 * 页面加载时调一次:如果上次点了「打开 App」却又回到了本页,说明没装 App。
 * 返回 true 表示该给 App Store 入口了(读完即清,不影响下一次)。
 */
export const consumeIosPending = () => {
  const s = session()
  if (!s) return false
  try {
    const hit = s.getItem(IOS_PENDING_KEY) === '1'
    if (hit) s.removeItem(IOS_PENDING_KEY)
    return hit
  } catch (e) {
    return false
  }
}

// 页面是否还在前台。唤起成功 → 浏览器被切到后台 → 这里返回 false
const stillHere = () => typeof document === 'undefined' || !document.hidden

// 装一次"页面是否被切走"的观察,返回 { left: () => boolean, dispose }
const watchLeaving = () => {
  let left = false
  const mark = () => {
    left = true
  }
  document.addEventListener('visibilitychange', mark)
  window.addEventListener('pagehide', mark)
  return {
    left: () => left || !stillHere(),
    dispose: () => {
      document.removeEventListener('visibilitychange', mark)
      window.removeEventListener('pagehide', mark)
    },
  }
}

/**
 * @param videoId      视频 id
 * @param viaLink      调用方是不是 <a href={buildUniversalLink(videoId)}> 本身。
 *                     是的话 iOS 分支**不自己改 location**,交给浏览器原生跟随链接 ——
 *                     用户直接点 <a> 是 iOS 上触发 universal link 最可靠的方式,
 *                     JS 改 location 在不同 iOS 版本上行为不一致。
 * @param onWechatTip  微信内:让页面弹「右上角···」引导蒙层
 * @param onNeedStore  iOS 没唤起成功:让页面给出 App Store 入口
 * @returns 本次走的分支(便于埋点)
 */
export const openApp = ({ videoId, viaLink = false, onWechatTip, onNeedStore }) => {
  if (isWeixin()) {
    onWechatTip && onWechatTip()
    return 'wechat_tip'
  }

  if (isIOS()) {
    const watcher = watchLeaving()
    // 装了 App:iOS 拦下这次导航,直接唤起 App 并把该 URL 交给它(App 侧 videoDeepLink.js
    // 从 /videoShare/<id> 解析出 id,跳单视频页)。没装:gateway 302 回本页 → 页面重载,
    // 下面这个定时器会随之丢掉,所以先落标记,由页面重载后 consumeIosPending 兜底。
    markIosPending()
    // viaLink 时链接由浏览器自己跟随(见上面 @param 的理由),这里不再改 location
    if (!viaLink) window.location.href = buildUniversalLink(videoId)
    setTimeout(() => {
      watcher.dispose()
      if (watcher.left()) {
        // 页面被切走 = iOS 拦下了这次导航、App 起来了。标记只服务于"没装 App →
        // gateway 302 回本页 → 页面重载"那一条路,这里必须撤掉,否则它会一直留在
        // sessionStorage 里,下次进页面误弹「还没有安装」(见 clearIosPending)。
        //
        // 只在这个回调里清、不挂 visibilitychange/pagehide 一清了之:没装 App 那条路
        // 本页会被 302 替换掉,pagehide 同样会触发 —— 抢在重载前清掉标记的话,
        // 兜底引导就永远弹不出来了。而这里的定时器随页面一起消失,天然只在
        // "本页活着"(= 唤起成功)时执行。
        clearIosPending()
        return
      }
      onNeedStore && onNeedStore()
    }, IOS_UL_TIMEOUT)
    return 'ios_universal_link'
  }

  if (isAndroid()) {
    const watcher = watchLeaving()
    window.location.href = buildSchemeUrl(videoId)

    setTimeout(() => {
      if (watcher.left()) {
        watcher.dispose()
        return
      }
      // 没唤起 App:退一步,试本机应用市场
      window.location.href = ANDROID_MARKET_URL
      setTimeout(() => {
        watcher.dispose()
        // 连市场都没唤起(没装市场/被厂商拦):落应用宝网页
        if (!watcher.left()) window.location.href = YINGYONGBAO_URL
      }, MARKET_TIMEOUT)
    }, SCHEME_TIMEOUT)
    return 'android_scheme'
  }

  // 桌面浏览器等:给应用宝页面,至少能扫码下载
  window.location.href = YINGYONGBAO_URL
  return 'fallback_market'
}



// WEBPACK FOOTER //
// ./src/modules/videoShare/openApp.js